(ns frontend.view.modals.association.events
  (:require
   [clojure.string :as string]
   [common.excel-import-templates.registry :as excel-import-templates]
   [common.localization :as loc]
   [common.schema.entity-type-names :as entity-type-names]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.controller.websocket.event :as ws]
   [frontend.exports.exceljs.sheet :as ex-sheet]
   [frontend.exports.exceljs.workbook :as ex-workbook]
   [frontend.logging :as logging]
   [frontend.utils :as fu]
   [frontend.view.modals.association.sub :as sub]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(defn form-ready?
  [state]
  (let [{:keys [target-id new-option association-types]} @state]
    (and (seq association-types)
         (or target-id new-option))))

(defn send
  [state entity-id association-types target-type on-success!]
  (let [todo-counter (atom (count association-types))]
    (doseq [association-type association-types]
      (entity-actions/*->add-association entity-id
                                         association-type
                                         target-type
                                         (when (number? (:target-id @state)) (:target-id @state))
                                         (:new-option @state)
                                         (:source-comment @state)
                                         nil
                                         (fn [_]
                                           (when (zero? (reset! todo-counter (dec @todo-counter)))
                                             (on-success!)))
                                         (fn [err]
                                           (logging/error "Failed to add association : " err))))))

(rf/reg-event-db
 ::set-selected-tab
 (fn [db [_ selected-tab]]
   (assoc db ::sub/selected-tab selected-tab)))

(rf/reg-event-db
 ::cleanup
 (fn [db _]
   (dissoc db
           ::sub/import-flow-started?
           ::sub/import-flow-step
           ::sub/uploading?
           ::sub/parsing-error?
           ::sub/creating-associations?
           ::sub/create-associations-error?
           ::sub/import-errors
           ::sub/import-data
           ::sub/selected-tab)))

(rf/reg-event-db
 ::find-entity-matches-success
 (fn [db [_ import-data response]]
   (let [entity-candidate-id->match (:body response)
         import-data-w-possible-matches
         (->> import-data
              (map (fn [data]
                     (let [entity-candidate-id (-> data
                                                   :entity-candidate
                                                   :db/id)
                           entity-match (get entity-candidate-id->match entity-candidate-id)]
                       (assoc data :entity-match entity-match)))))]
     (-> db
         (dissoc ::sub/uploading?)
         (assoc ::sub/import-flow-started? true)
         (assoc ::sub/import-data import-data-w-possible-matches)
         (assoc ::sub/import-flow-step :import-success)))))

(rf/reg-event-db
 ::find-entity-matches-error
 (fn [db [_ response]]
   (let [body (:body response)]
     (js/console.log "Error finding matches : " body)
     (-> db
         (dissoc ::sub/uploading?)
         (assoc ::sub/import-flow-started? true)
         (assoc ::sub/import-flow-step :find-matches-error)))))

(rf/reg-event-fx
 ::find-entity-matches
 (fn [{:keys [db]} [_ data]]
   (let [entity-candidates (->> data
                                (mapv :entity-candidate))]
     {:db       db
      ::ws/send {:id         :entity/find-matches
                 :body       {:entity-candidates entity-candidates}
                 :on-success [::find-entity-matches-success data]
                 :on-error   [::find-entity-matches-error]}})))

(defn create-import-template-file-name
  [source-entity-type target-entity-type]
  (let [source-name (loc/tr source-entity-type)
        target-name-plural (entity-type-names/get-plural target-entity-type :en)
        whitespace-separated-name
        (str "import " source-name " associated " target-name-plural " template")
        name-parts (string/split whitespace-separated-name #" ")
        file-name (->> name-parts
                       (string/join "-")
                       (string/lower-case))]
    file-name))

(defn download-import-excel
  [{:keys [entity-type target-type]}]
  (let [[wb sheet] (ex-workbook/create-workbook-with-sheet "Import contacts")
        import-template (excel-import-templates/get-template {:source-entity-type entity-type
                                                              :target-entity-type target-type})
        columns (:columns import-template)
        association-type-options (:association-type-options import-template)

        association-type-column (->> columns
                                     (filter (fn [{:keys [column-type]}]
                                               (= column-type :association-type)))
                                     (first))
        column-labels (concat
                       (->> columns
                            (map (fn [{:keys [label mandatory?]}]
                                   (str label
                                        (when mandatory?
                                          (str " (" (string/lower-case (loc/tr :mandatory)) ")"))))))
                       [""]
                       [(loc/tr :options-for [(:label association-type-column)])])

        file-name (create-import-template-file-name entity-type target-type)]

    (ex-sheet/set-columns sheet column-labels)

    (->> association-type-options
         (mapv (fn [{:keys [label]}]
                 (concat
                  (->> column-labels
                       (butlast)
                       (map (fn [_] "")))
                  [label])))
         (ex-sheet/add-rows sheet))

    (ex-workbook/save-workbook wb file-name)))


;; Limit rows to 50
;; Stop reading after empty row

(defn extract-excel-data
  [{:keys [file entity-type target-type]} on-success on-error]
  (let [import-template (excel-import-templates/get-template {:source-entity-type entity-type
                                                              :target-entity-type target-type})
        columns (:columns import-template)
        association-type-label->value (:association-type-label->value import-template)

        validate-entity-data (fn [entity-candidate]
                               (case target-type
                                 :entity.type/person
                                 (let [email (:person/universal-email-address entity-candidate)
                                       valid-email? (fu/validate-email email)]
                                   (cond (cu/empty-value? email)
                                         {:person/universal-email-address [(loc/tr :required)]}
                                         (not valid-email?)
                                         {:person/universal-email-address [(loc/tr :invalid-email)]}))))

        identifying-attribute (case target-type
                                :entity.type/person :person/universal-email-address)

        group-by-identifying-attribute
        (fn [row-datas]
          (let [grouped
                (->> row-datas
                     (group-by
                      (fn [{:keys [entity-candidate]}]
                        (get entity-candidate identifying-attribute))))]
            grouped))

        wb (ex-workbook/create-workbook)
        xlsx (aget wb "xlsx")
        load-promise (.call (aget xlsx "load") xlsx file)
        extract-state (r/atom {})
        should-stop-reading-state (r/atom false)]
    (->
     load-promise
     (.then
      (fn []
        (let [get-worksheet-fn (aget wb "getWorksheet")
              sheet (.call get-worksheet-fn wb 1)
              each-row-fn (aget sheet "eachRow")]
          (.call
           each-row-fn
           sheet
           (fn [row row-number]
             (when-not (or (= row-number 1)
                           @should-stop-reading-state)
               (if (< 51 row-number)
                 (do
                   (reset! should-stop-reading-state true)
                   (on-error :data-import-error-over-50-rows))
                 (let [get-cell-fn (aget row "getCell")

                       columns-with-values
                       (->> columns
                            (map-indexed
                             (fn [index column-def]
                               (let [cell (.call get-cell-fn row (inc index))
                                     text-value (aget cell "text")
                                     value (if (= text-value "")
                                             nil
                                             text-value)]
                                 (assoc column-def :value value)))))

                       import-entity-data
                       (into {:db/id       (str "entity-candidate-" row-number)
                              :entity/type target-type}
                             (->> columns-with-values
                                  (keep (fn [{:keys [attribute value column-type]}]
                                          (when (= column-type :entity-data)
                                            [attribute value])))))

                       import-entity-errors (validate-entity-data import-entity-data)

                       association-comment (->> columns-with-values
                                                (keep (fn [{:keys [column-type value]}]
                                                        (when (= column-type :association-comment)
                                                          value)))
                                                (first))

                       association-type-labels
                       (let [value (->> columns-with-values
                                        (keep (fn [{:keys [column-type value]}]
                                                (when (= column-type :association-type)
                                                  value)))
                                        (first))
                             labels-str (-> value
                                            (str)
                                            (string/split #";"))]
                         (mapv string/trim labels-str))

                       association-type-missing? (every? cu/empty-value? association-type-labels)
                       empty-label? (some cu/empty-value? association-type-labels)

                       association-types-with-labels
                       (when-not association-type-missing?
                         (->> association-type-labels
                              (map (fn [label]
                                     (let [value (get association-type-label->value label)]
                                       {:label label
                                        :value value})))))

                       invalid-association-type-labels
                       (->> association-types-with-labels
                            (keep (fn [{:keys [value label]}]
                                    (when (and (not (cu/empty-value? label))
                                               (cu/empty-value? value))
                                      label))))

                       association-type-errors
                       (concat
                        (when (or association-type-missing?
                                  empty-label?)
                          [(loc/tr :empty-values-not-allowed)])

                        (when (seq invalid-association-type-labels)
                          (let [as-str (string/join "; " invalid-association-type-labels)]
                            (if (< 1 (count invalid-association-type-labels))
                              [(str (loc/tr :invalid-values) ": " as-str)]
                              [(str (loc/tr :invalid-value) ": " as-str)]))))

                       import-errors
                       (merge
                        (when-not (empty? import-entity-errors)
                          import-entity-errors)

                        (when (seq association-type-errors)
                          {:association-type association-type-errors}))

                       association-types (->> association-types-with-labels
                                              (map (fn [{:keys [value]}]
                                                     value))
                                              (set))

                       empty-row? (->> columns-with-values
                                       (mapv :value)
                                       (every? cu/empty-value?))

                       row-data {:row-number              row-number
                                 :entity-candidate        import-entity-data
                                 :association-types       association-types
                                 :association-type-labels association-type-labels
                                 :association-comment     association-comment
                                 :import-errors           import-errors}]
                   (if empty-row?
                     (reset! should-stop-reading-state true)
                     (swap! extract-state assoc-in [:data row-number] row-data))))))))))
     (.then
      (fn []
        (let [import-data (-> @extract-state
                              :data
                              (vals)
                              (doall))]
          (if (= (count import-data) 0)
            (on-error :data-import-error-no-rows-found)
            (let [import-data
                  (->> import-data
                       (group-by-identifying-attribute)
                       (mapcat
                        (fn [[_ row-data-arr]]
                          (if (= (count row-data-arr) 1)
                            [(first row-data-arr)]
                            (let [duplicate-row-numbers (map :row-number row-data-arr)]
                              (->> row-data-arr
                                   (map
                                    (fn [row-data]
                                      (let [duplicate-rows
                                            (->> duplicate-row-numbers
                                                 (remove (fn [row-number]
                                                           (= row-number (:row-number row-data)))))
                                            duplicate-rows-error
                                            (if (< 1 (count duplicate-rows))
                                              (loc/tr :duplicate-values-found-from-rows [(string/join ", " duplicate-rows)])
                                              (loc/tr :duplicate-values-found-from-row [(first duplicate-rows)]))]
                                        (update-in row-data
                                                   [:import-errors identifying-attribute]
                                                   conj
                                                   duplicate-rows-error)))))))))
                       (vec))]
              (on-success import-data))))))
     (.catch
      (fn [e]
        (js/console.log "Caught error: " e)
        (on-error :data-import-error-unknown-error))))))

(rf/reg-event-db
 ::set-parsing-error
 (fn [db [_ parsing-error?]]
   (assoc db ::sub/parsing-error? parsing-error?)))

(rf/reg-event-db
 ::set-data-import-error
 (fn [db [_ error-reason]]
   (if error-reason
     (assoc db ::sub/data-import-error error-reason)
     (dissoc db ::sub/data-import-error))))


(rf/reg-fx
 ::extract-data
 (fn [[file entity-type target-type]]
   (extract-excel-data {:file        file
                        :entity-type entity-type
                        :target-type target-type}
                       (fn [import-data]
                         (rf/dispatch [::find-entity-matches import-data]))
                       (fn [error-reason]
                         (rf/dispatch [::set-data-import-error error-reason])))))

(rf/reg-event-fx
 ::upload-file
 (fn [{:keys [db]} [_ file entity-type target-type]]
   {:db            (assoc db
                     ::sub/uploading? true
                     ::sub/parsing-error? false
                     ::sub/data-import-error nil)
    ::extract-data [file entity-type target-type]}))

(rf/reg-fx
 ::call-success-fn
 (fn [on-success!]
   (on-success!)))

(rf/reg-event-fx
 ::create-associations-success
 (fn [{:keys [db]} [_ on-success! _response]]
   (let [new-db (-> db
                    (dissoc ::sub/creating-associations?
                            ::sub/create-associations-error?))]
     {:db               new-db
      ::call-success-fn on-success!})))

(rf/reg-event-db
 ::create-associations-error
 (fn [db [_ response]]
   (let [error-msg (:message response)]
     (js/console.log "Failed to create associations : " error-msg)
     (-> db
         (dissoc ::sub/creating-associations?)
         (assoc ::sub/create-associations-error? true)))))

(rf/reg-event-fx
 ::create-associations
 (fn [{:keys [db]} [_ source-entity-id on-success!]]
   (let [import-data (get db ::sub/import-data)
         targets (->> import-data
                      (mapv (fn [{:keys [entity-candidate entity-match
                                         association-comment
                                         association-types]}]
                              (let [entity (or entity-match entity-candidate)]
                                (merge
                                 {:entity            entity
                                  :association-types (set association-types)}

                                 (when-not (cu/empty-value? association-comment)
                                   {:source-comment association-comment}))))))]

     {:db       (assoc db ::sub/creating-associations? true)
      ::ws/send {:id         :entity/mass-add-associations
                 :body       {:source-id source-entity-id
                              :targets   targets}
                 :on-success [::create-associations-success on-success!]
                 :on-error   [::create-associations-error]}})))


